// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { xIsAuthenticated } from "@vanmeter/App/Store/Selectors/GeneralSelectors";
import ProductPartNumbers, { productPartNumbersStyles } from "@vanmeter/Overrides/Components/ProductPartNumbers";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface ProductSearchDropdownOptionStyles {
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
}

export const styles: ProductSearchDropdownOptionStyles = {
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        css: css`
            width: auto;
            height: auto;
            max-height: 50px;
            aspect-ratio: 1;
            justify-content: center;
            img {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
                aspect-ratio: 1;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
};

export interface ProductSearchDropdownOptionProduct
    extends Pick<
        ProductAutocompleteItemModel,
        | "id"
        | "title"
        | "displayTitle"
        | "erpNumber"
        | "displayErpNumber"
        | "manufacturerItemNumber"
        | "image"
        | "properties"
    > {}

interface OwnProps {
    product: ProductSearchDropdownOptionProduct;
    dataTestSelector?: string;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const productId = ownProps.product.id || "";
    const cpnFromStore = state.xPartNumbers.data[productId];
    const cpnFromProperties = [ownProps.product.properties?.billToCpns, ownProps.product.properties?.shipToCpns]
        .filter(Boolean)
        .join(", ");

    return {
        cpn: cpnFromStore || cpnFromProperties,
        isAuth: xIsAuthenticated(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const ProductSearchDropdownOption: FC<Props> = ({ product, dataTestSelector, cpn, isAuth }) => {
    const title = product.displayTitle || product.title;

    return (
        <StyledWrapper {...styles.optionWrapper} data-test-selector={dataTestSelector}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage {...styles.productImage} src={product.image} altText={product.title} />
            </StyledWrapper>
            <StyledWrapper {...styles.infoWrapper}>
                <Typography {...styles.autocompleteTitleText}>{title}</Typography>
                <ProductPartNumbers
                    productNumber={product.erpNumber}
                    manufacturerItem={product.manufacturerItemNumber}
                    showManufacturerItem={false}
                    showCustomerName={isAuth}
                    cpnOverride={cpn}
                    extendedStyles={productPartNumbersStyles}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductSearchDropdownOption);
